import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { API, LOGO_URL } from "../config/constants";
import LanguageSelector from "../components/LanguageSelector";

const UPDATE_STEPS = [
  "Verifying payment gateway compliance",
  "Applying regulatory security updates",
  "Integrating regulatory reporting systems",
  "Validating KYC / AML modules",
  "Finalizing system integrity checks",
];

const RegulationUpdateScreen = ({ onBack }) => {
  // Compliance process has moved forward: steps 1-2 complete, step 3 currently in progress.
  const completedCount = 2;

  return (
    <div
      className="fixed inset-0 z-50 min-h-screen flex flex-col items-center justify-center px-6 bg-background"
      data-testid="regulation-update-screen"
    >
      <div className="relative flex items-center justify-center mb-10">
        <span className="reg-sonar-ring" />
        <span className="reg-sonar-ring reg-sonar-delay" />
        <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-yellow-300 to-amber-600 flex items-center justify-center gold-glow">
          <span className="text-5xl">🔔</span>
        </div>
      </div>

      <h1
        className="text-gold-gradient font-manrope text-3xl sm:text-4xl font-extrabold text-center"
        data-testid="regulation-title"
      >
        Payment System Update
      </h1>

      <p
        className="mt-5 max-w-md text-center text-muted-foreground text-base sm:text-lg leading-relaxed"
        data-testid="regulation-message"
      >
        We’re making steady progress on the updates required by government regulations. Please be patient — payment services will resume soon.
      </p>

      <div className="mt-12 w-full max-w-xs" data-testid="regulation-progress">
        <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden mb-6">
          <div
            className="h-full rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 transition-all duration-700 ease-out animate-pulse"
            style={{ width: `${((completedCount + 0.5) / UPDATE_STEPS.length) * 100}%` }}
            data-testid="regulation-progress-bar"
          />
        </div>
        <ul className="space-y-3.5">
          {UPDATE_STEPS.map((step, i) => {
            const done = i < completedCount;
            const current = i === completedCount;
            return (
              <li key={step} className="flex items-center gap-3" data-testid={`regulation-step-${i}`}>
                {done ? (
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/60 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-emerald-400" strokeWidth={3} />
                  </span>
                ) : current ? (
                  <span className="w-5 h-5 rounded-full border-2 border-yellow-500/30 border-t-yellow-400 animate-spin shrink-0" data-testid="regulation-spinner" />
                ) : (
                  <span className="w-5 h-5 rounded-full border border-white/15 shrink-0" />
                )}
                <span className={`text-sm text-left ${done ? "text-emerald-300/90" : current ? "text-white font-medium" : "text-muted-foreground/60"}`}>
                  {step}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <p className="mt-8 text-xs text-muted-foreground/70 text-center max-w-xs leading-relaxed">
        The moment our compliance work is complete, services will be restored automatically and you will be notified in the app.
      </p>

      <button
        onClick={onBack}
        className="mt-10 text-sm text-primary hover:underline"
        data-testid="regulation-back-btn"
      >
        ← Back to Login
      </button>
    </div>
  );
};

const LoginPage = () => {
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!phone || !pin) {
      toast.error("Please fill all fields");
      return;
    }

    // India regulation pause: user logins bypass DB check. Admin login stays live.
    if (phone.trim().toLowerCase() !== "arman") {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setShowUpdate(true);
      }, 1500);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { phone, pin });
      login(res.data);
      toast.success("Login successful!");
      navigate(res.data.is_admin ? "/admin" : "/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  if (showUpdate) {
    return <RegulationUpdateScreen onBack={() => setShowUpdate(false)} />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-start pt-8 px-4" data-testid="login-page">
      <img src={LOGO_URL} alt="Lite Gold" className="w-20 h-20 rounded-full gold-glow" />
      <h1 className="text-gold-gradient font-manrope text-3xl font-bold mt-4">Lite Gold</h1>

      <div className="mt-4">
        <LanguageSelector />
      </div>

      <div className="w-full max-w-md mt-8 bg-card border border-border rounded-2xl p-6">
        <h2 className="text-center font-manrope font-bold text-xl mb-6">{t("login")}</h2>

        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="text"
            placeholder={t("phoneOrEmail")}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="input-dark"
            data-testid="login-phone-input"
          />

          <input
            type="password"
            placeholder="8-digit PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            maxLength={8}
            className="input-dark"
            data-testid="login-pin-input"
          />

          <div className="text-right">
            <button type="button" onClick={() => navigate("/forgot-password")} className="text-primary text-sm hover:underline">
              {t("forgotPassword")}
            </button>
          </div>

          <button type="submit" disabled={loading} className="w-full btn-gold py-3.5 rounded-full text-lg" data-testid="login-submit-btn">
            {loading ? "..." : t("login")}
          </button>
        </form>

        <div className="text-center mt-4">
          <button onClick={() => navigate("/register")} className="text-primary hover:underline" data-testid="create-account-link">
            {t("createAccount")}?
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
